import { Alert, Button, Footer, showDialog } from '@bpinternal/ui-kit-next'
import { ArrowDownSquare, Image, ImageUp } from 'lucide-react'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useUploadFile } from '~/hooks'
import { contentType } from 'mime-types'
import { Spinner } from '~/elements'

export function ImageInput({
  workspaceId,
  botId,
  title,
  imgKey,
  imgUrl,
  setImgUrl,
}: {
  workspaceId: string
  botId: string
  title: string
  imgKey: string
  imgUrl: string
  setImgUrl: (url: string | undefined) => void
}) {
  return (
    <div
      className="group relative cursor-pointer"
      onMouseDown={() =>
        showDialog(
          (props) => (
            <UploadImageDialog
              {...props}
              workspaceId={workspaceId}
              botId={botId}
              imgKey={imgKey}
              imgUrl={imgUrl ?? ''}
              setImgUrl={setImgUrl}
            />
          ),
          { title }
        )
      }
    >
      <div className="absolute inset-0 z-10 flex w-11 items-center justify-center rounded-full bg-neutral-900 opacity-0 transition-opacity group-hover:opacity-70">
        <ImageUp className="h-5 w-5 text-neutral-500" />
      </div>
      {imgUrl ? (
        <img className="aspect-square h-11 max-w-none rounded-full object-cover" src={imgUrl} alt="FAB Image" />
      ) : (
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-neutral-100 transition-opacity group-hover:opacity-0">
          <Image className="h-5 w-5 text-neutral-500" />
        </div>
      )}
    </div>
  )
}

export function UploadImageDialog({
  workspaceId,
  botId,
  imgKey,
  imgUrl,
  setImgUrl,
  close,
}: {
  workspaceId: string
  botId: string
  imgKey: string
  imgUrl: string
  setImgUrl: (url: string | undefined) => void
  close: () => void
}) {
  const [newImgUrl, setNewImgUrl] = useState<string | undefined>(imgUrl ?? undefined)

  return (
    <div className="flex w-full flex-col">
      <UploadImageForm
        workspaceId={workspaceId}
        botId={botId}
        imgKey={imgKey}
        imgUrl={newImgUrl}
        setImgUrl={setNewImgUrl}
      />
      <div className="mt-4 flex items-baseline justify-between gap-4">
        <Button
          variant={'link'}
          onClick={() => {
            setImgUrl(undefined)
            close()
          }}
        >
          Reset to default
        </Button>
        <Footer
          disabled={newImgUrl === imgUrl}
          onConfirm={() => {
            setImgUrl(newImgUrl)
            close()
          }}
          onCancel={close}
        />
      </div>
    </div>
  )
}

export type UploadImageProps = {
  botId: string
  workspaceId: string
  imgKey: string
  imgUrl?: string
  setImgUrl: (url: string | undefined) => void
}

function UploadImageForm(props: UploadImageProps) {
  const {
    mutateAsync: uploadFile,
    error,
    isPending,
  } = useUploadFile({ botId: props.botId, workspaceId: props.workspaceId })

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/svg+xml': ['.svg'],
    },
    onDrop: (acceptedFiles) => {
      const mimeType = contentType(acceptedFiles[0]?.type ?? '')
      uploadFile({
        key: `bot-${props.botId}-${props.imgKey}`,
        content: acceptedFiles[0],
        accessPolicies: ['public_content'],
        contentType: mimeType ? mimeType : undefined,
        tags: { source: 'integration', integration: 'webchat' },
      }).then((file) => {
        props.setImgUrl(file?.file.url)
      })
    },
  })

  return (
    <div {...getRootProps()} className="w-full">
      <>
        <input {...getInputProps()} />
        <div className="relative flex min-h-[150px] items-center rounded-lg border-2 border-dotted border-neutral-200 p-5">
          {isPending ? (
            <div className="flex w-full justify-center">
              <Spinner size="5" color="gray" />
            </div>
          ) : (
            <>
              {isDragActive && (
                <div className="absolute left-0 top-0 z-[2] flex h-full w-full flex-col items-center justify-center bg-neutral-50/80 text-neutral-900">
                  <ArrowDownSquare className="mb-4 h-8 w-8 animate-bounce opacity-50" />
                </div>
              )}
              <>
                <div className="z-1 relative h-[120px] w-[120px]">
                  {props.imgUrl && (
                    <>
                      {/* Fake mask */}
                      <div className="z-1 absolute left-0 top-0 h-full w-full rounded-sm">
                        <img className="absolute h-full w-full object-cover" src={props.imgUrl} alt="Bot Avatar" />
                        <div className="absolute h-full w-full bg-black/50" />
                      </div>
                      <img
                        className="relative aspect-square h-full w-full max-w-none rounded-full border border-neutral-200 bg-neutral-50 object-cover"
                        src={props.imgUrl}
                        alt="Bot Avatar"
                      />
                    </>
                  )}
                </div>
                <div className="mx-6 h-[96px] w-[1px] bg-neutral-200" />
                <div className="flex flex-grow flex-col items-center">
                  <span className="text-sm text-neutral-900">Drop image file here</span>
                  <span className="mb-2 mt-1 text-sm text-neutral-800">or</span>
                  <Button variant="outline" size={'sm'}>
                    Select File
                  </Button>
                </div>
              </>
            </>
          )}
        </div>
        {error && (
          <Alert className="mt-4 p-2" variant="destructive">
            {error.message}
          </Alert>
        )}
      </>
    </div>
  )
}
